import { apiService } from './api';

export interface Stock {
  id: number;
  symbol: string;
  exchange: string;
  created_at: string;
}

export interface WatchlistItem extends Stock {
  owner_id: string;
  owner_name: string;
  is_mine: boolean;
}

export interface WatchlistUser {
  id: string;
  name: string;
  email: string;
  count: number;
  is_current_user: boolean;
}

export interface StockPrice {
  id: number;
  stock_id: number;
  stock_symbol: string;
  price: number;
  change: number;
  change_percent: number;
  volume: number;
  market_cap?: number;
  pe_ratio?: number;
  timestamp: string;
}

export interface StockSearchResult {
  symbol: string;
  exchange: string;
}

export interface TechnicalAnalysis {
  id: string;
  stock_id: string;
  stock_symbol: string;
  analysis_date: string;
  last_trade_price: number;
  lifetime_high: number;
  lifetime_low: number;
  one_year_high: number;
  one_year_low: number;
  month_high: number;
  month_low: number;
  week_high: number;
  week_low: number;
  one_year_return: number;
  month_return: number;
  week_return: number;
  day_return: number;
  last_golden_crossover: string | null;
  last_death_crossover: string | null;
  gco_change: number;
  dco_change: number;
  gco_hit_rate: number;
  gco_avg_return: number;
  gco_count: number;
  gco_return_std_dev: number;
  gco_acce_point: number;
  gco_acce_hit_rate: number;
  dco_count: number;
  n_swing_10: number;
  d_swing_10: number;
  v_swing_10: number;
  n_swing_15: number;
  d_swing_15: number;
  v_swing_15: number;
  n_swing_20: number;
  d_swing_20: number;
  v_swing_20: number;
  market_cap: number | null;
  created_at: string;
  updated_at: string;
}

export interface TechnicalAnalysisWithStock extends TechnicalAnalysis {
  stock_name: string;
  stock_exchange: string;
  stock_sector: string;
}

export interface TechnicalAnalysisSummary {
  stocks: TechnicalAnalysisWithStock[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

// Support/Resistance level with TradingView-style data
export interface SupportResistanceLevel {
  price: number;
  touch_count: number;
  significance: number;
  first_date: string;
  last_date: string;
  type: 'support' | 'resistance';
  distance_pct: number;
}

export interface SupportResistanceData {
  levels: SupportResistanceLevel[];
  support: number | null;
  resistance: number | null;
  pivot_points: Array<{
    date: string;
    price: number;
    type: 'support' | 'resistance';
  }>;
  current_price?: number;
  analysis?: {
    total_resistance_levels: number;
    total_support_levels: number;
    strongest_resistance: SupportResistanceLevel | null;
    strongest_support: SupportResistanceLevel | null;
  };
}

export interface EnhancedChartData {
  symbol: string;
  period: string;
  data: any[];
  indicators: {
    [key: string]: number[];
  };
  support_resistance: SupportResistanceData;
  patterns: {
    [key: string]: Array<{
      date: string;
      price: number;
    }>;
  };
  chart_type?: 'line' | 'candlestick';
  data_source?: string;
  last_updated?: string;
  available_indicators?: string[];
}

export interface StockAdjustment {
  id: string;
  stock_id: string;
  stock_symbol: string;
  adjustment_date: string;
  adjustment_type: 'stock_split' | 'bonus_shares';
  ratio: number;
  dry_run: boolean;
  notes?: string;
  created_at: string;
  updated_at: string;
}

/** Encode a stock symbol for use in URL paths (handles & and other special chars) */
const enc = (symbol: string) => encodeURIComponent(symbol);

class StockService {
  async getAllStocks(): Promise<Stock[]> {
    const response = await apiService.get<Stock[]>('/stocks/');
    return response;
  }

  async getStock(symbol: string): Promise<Stock> {
    const response = await apiService.get<{ stock: Stock }>(`/stocks/${enc(symbol)}`);
    return response.stock;
  }

  async getStockPrices(symbol: string, days: number = 30): Promise<any[]> {
    const response = await apiService.get<{ symbol: string; prices: any[] }>(`/stocks/${enc(symbol)}/prices?days=${days}`);
    return response.prices || [];
  }

  async getLatestPrices(): Promise<StockPrice[]> {
    return apiService.get<StockPrice[]>('/stocks/prices/latest');
  }

  async getLatestPrice(symbol: string): Promise<any> {
    return apiService.get<any>(`/stocks/${enc(symbol)}/latest`);
  }

  async searchStocks(query: string): Promise<StockSearchResult[]> {
    return apiService.get<StockSearchResult[]>(`/stocks/search?q=${encodeURIComponent(query)}`);
  }

  async addStock(symbol: string): Promise<Stock> {
    return apiService.post<Stock>('/stocks', { symbol });
  }

  async refreshPrices(): Promise<{ message: string; updated_count: number }> {
    return apiService.post<{ message: string; updated_count: number }>('/stocks/refresh');
  }

  async getWatchlist(userFilter: string = 'mine'): Promise<WatchlistItem[]> {
    return apiService.get<WatchlistItem[]>(`/stocks/watchlist?user_filter=${userFilter}`);
  }

  async getWatchlistUsers(): Promise<WatchlistUser[]> {
    return apiService.get<WatchlistUser[]>('/stocks/watchlist/users');
  }

  async addToWatchlist(symbol: string): Promise<{ message: string }> {
    return apiService.post<{ message: string }>('/stocks/watchlist', { symbol });
  }

  async removeFromWatchlist(symbol: string): Promise<{ message: string }> {
    return apiService.delete<{ message: string }>(`/stocks/watchlist/${enc(symbol)}`);
  }

  async getTechnicalAnalysis(symbol: string): Promise<TechnicalAnalysis | null> {
    try {
      const response = await apiService.get<TechnicalAnalysis>(`/stocks/${enc(symbol)}/technical-analysis`);
      return response;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async getTechnicalAnalysisSummary(filters?: {
    search?: string;
    sector?: string;
    exchange?: string;
    min_price?: number;
    max_price?: number;
    min_return?: number;
    max_return?: number;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
    page?: number;
    per_page?: number;
  }): Promise<TechnicalAnalysisSummary> {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
    }

    const response = await apiService.get<TechnicalAnalysisSummary>(
      `/stocks/technical-analysis-summary?${params.toString()}`
    );
    return response;
  }

  async runTechnicalAnalysis(symbol?: string): Promise<{ message: string; processed?: number; failed?: number; total?: number }> {
    const response = await apiService.post<{ message: string; processed?: number; failed?: number; total?: number }>(
      `/stocks/run-technical-analysis`,
      symbol ? { symbol } : {}
    );
    return response;
  }

  async getChartData(symbol: string, period: string = '1y'): Promise<any> {
    return apiService.get<any>(`/stocks/${enc(symbol)}/chart-data?period=${period}`);
  }

  async getEnhancedChartData(symbol: string, days: number = 365, indicators: string[] = []): Promise<EnhancedChartData> {
    const params = new URLSearchParams();
    params.append('days', days.toString());
    indicators.forEach(indicator => params.append('indicators', indicator));

    return apiService.get<EnhancedChartData>(
      `/stocks/${enc(symbol)}/chart-data-enhanced?${params.toString()}`
    );
  }

  async getChartDataDbOnly(symbol: string, days: number = 90, indicators: string[] = []): Promise<EnhancedChartData> {
    const params = new URLSearchParams();
    params.append('days', days.toString());
    indicators.forEach(indicator => params.append('indicators', indicator));

    return apiService.get<EnhancedChartData>(
      `/stocks/${enc(symbol)}/chart-data-db-only?${params.toString()}`
    );
  }

  async getEnhancedChartDataWithAllIndicators(symbol: string, days: number = 365, indicators: string[] = []): Promise<EnhancedChartData> {
    const params = new URLSearchParams();
    params.append('days', days.toString());
    indicators.forEach(indicator => params.append('indicators', indicator));

    return apiService.get<EnhancedChartData>(
      `/stocks/${enc(symbol)}/enhanced-chart-data?${params.toString()}`
    );
  }

  // New methods for file-based analysis and manual updates
  async getAnalysisFromFile(symbol: string): Promise<any> {
    return apiService.get<any>(`/stocks/${enc(symbol)}/analysis-from-file`);
  }

  async getAnalysisSummary(): Promise<any> {
    return apiService.get<any>('/stocks/analysis-summary');
  }

  async triggerManualPriceUpdate(options?: { symbol?: string, favoritesOnly?: boolean } | string): Promise<any> {
    // Handle backward compatibility where argument was just a string (symbol)
    let payload = {};
    if (typeof options === 'string') {
      payload = { symbol: options };
    } else if (options) {
      payload = options;
    }
    return apiService.post('/stocks/update-prices-manual', payload);
  }

  async triggerManualTechnicalAnalysis(): Promise<any> {
    return apiService.post('/stocks/run-consolidated-analysis', {});
  }

  async triggerScraping(): Promise<any> {
    return apiService.post('/stocks/scrape-screener-data', {});
  }

  async getTaskStatus(taskId?: string): Promise<any> {
    if (taskId) {
      return apiService.get<any>(`/stocks/tasks/${taskId}/status`);
    } else {
      // Get all async tasks
      return apiService.get('/stocks/tasks');
    }
  }

  async getSchedulerStatus(): Promise<any> {
    return apiService.get('/stocks/scheduler/status');
  }

  async getAllTasks(): Promise<{ tasks: any; active_count: number; total_count: number }> {
    return apiService.get<{ tasks: any; active_count: number; total_count: number }>('/stocks/tasks');
  }

  // Swing analysis methods
  async calculateCustomSwingPoints(symbol: string, peakPct: number, troughPct: number, days: number = 730): Promise<any> {
    return apiService.post(`/stocks/${enc(symbol)}/swing-analysis`, {
      peakPct,
      troughPct,
      days
    });
  }

  // Analysis data methods
  async getConsolidatedAnalysis(filter?: string, limit?: number, offset?: number, sort_by?: string, sort_order?: string): Promise<any> {
    const params = new URLSearchParams();
    if (filter) params.append('filter', filter);
    if (limit) params.append('limit', limit.toString());
    if (offset) params.append('offset', offset.toString());
    if (sort_by) params.append('sort_by', sort_by);
    if (sort_order) params.append('sort_order', sort_order);

    const queryString = params.toString();
    const url = `/stocks/consolidated-analysis${queryString ? `?${queryString}` : ''}`;

    return apiService.get(url);
  }

  // Enhanced analysis data methods with fundamental data
  async getConsolidatedAnalysisEnhanced(filter?: string, limit?: number, offset?: number, sort_by?: string, sort_order?: string): Promise<any> {
    const params = new URLSearchParams();
    if (filter) params.append('filter', filter);
    if (limit) params.append('limit', limit.toString());
    if (offset) params.append('offset', offset.toString());
    if (sort_by) params.append('sort_by', sort_by);
    if (sort_order) params.append('sort_order', sort_order);

    const queryString = params.toString();
    const url = `/stocks/consolidated-analysis-enhanced${queryString ? `?${queryString}` : ''}`;

    return apiService.get(url);
  }

  // Auto-update and system methods
  async getStockDetailsWithUpdate(symbol: string): Promise<any> {
    return apiService.get<any>(`/stocks/${enc(symbol)}/details-with-update`);
  }

  async getSystemStatus(): Promise<any> {
    return apiService.get<any>('/stocks/system/status');
  }

  async getSystemHealth(): Promise<any> {
    return apiService.get<any>('/stocks/system/health');
  }

  // Fundamental data methods
  async getFundamentalData(symbol: string): Promise<any> {
    return apiService.get<any>(`/stocks/fundamentals/${enc(symbol)}`);
  }

  async getScrapedData(symbol: string): Promise<any> {
    return apiService.get<any>(`/stocks/scraped-data/${enc(symbol)}`);
  }

  // Instant scraping method
  async scrapeSpecificStock(symbol: string): Promise<any> {
    return apiService.post<any>(`/stocks/scrape-specific`, { symbol });
  }

  // Stock Adjustment methods
  async getStockAdjustments(symbol: string): Promise<StockAdjustment[]> {
    return apiService.get<StockAdjustment[]>(`/stocks/stocks/${enc(symbol)}/adjustments`);
  }

  async addStockAdjustment(
    symbol: string,
    adjustment: {
      adjustment_date: string;
      adjustment_type: 'stock_split' | 'bonus_shares';
      ratio: number;
      dry_run?: boolean;
      notes?: string;
    }
  ): Promise<{ message: string; adjustment: StockAdjustment; normalization: any }> {
    return apiService.post<{ message: string; adjustment: StockAdjustment; normalization: any }>(
      `/stocks/stocks/${enc(symbol)}/adjustments`,
      adjustment
    );
  }

  async updateStockAdjustment(
    symbol: string,
    adjustmentId: string,
    adjustment: Partial<{
      adjustment_date: string;
      adjustment_type: 'stock_split' | 'bonus_shares';
      ratio: number;
      dry_run: boolean;
      notes: string;
    }>
  ): Promise<{ message: string; adjustment: StockAdjustment; normalization: any }> {
    return apiService.put<{ message: string; adjustment: StockAdjustment; normalization: any }>(
      `/stocks/stocks/${enc(symbol)}/adjustments/${adjustmentId}`,
      adjustment
    );
  }

  async deleteStockAdjustment(
    symbol: string,
    adjustmentId: string
  ): Promise<{ message: string; normalization: any }> {
    return apiService.delete<{ message: string; normalization: any }>(
      `/stocks/stocks/${enc(symbol)}/adjustments/${adjustmentId}`
    );
  }

  // ============================================================================
  // EVENT ANALYSIS METHODS
  // ============================================================================

  async getMarketEvents(includeInactive: boolean = false): Promise<any> {
    return apiService.get<any>(`/events?include_inactive=${includeInactive}`);
  }

  async createMarketEvent(event: {
    name: string;
    event_date: string;
    description?: string;
    category?: string;
    analysis_days_before?: number;
    analysis_days_after?: number;
  }): Promise<any> {
    return apiService.post<any>('/events', event);
  }

  async updateMarketEvent(eventId: string, updates: any): Promise<any> {
    return apiService.put<any>(`/events/${eventId}`, updates);
  }

  async deleteMarketEvent(eventId: string): Promise<any> {
    return apiService.delete<any>(`/events/${eventId}`);
  }

  async getEventAnalysis(eventId: string, options?: {
    min_score?: number;
    limit?: number;
    sort_by?: string;
    sort_order?: string;
  }): Promise<any> {
    const params = new URLSearchParams();
    if (options?.min_score) params.append('min_score', options.min_score.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.sort_by) params.append('sort_by', options.sort_by);
    if (options?.sort_order) params.append('sort_order', options.sort_order);
    
    const queryString = params.toString();
    return apiService.get<any>(`/events/${eventId}/analysis${queryString ? `?${queryString}` : ''}`);
  }

  async triggerEventAnalysis(eventId: string, stockIds?: string[]): Promise<any> {
    return apiService.post<any>(`/events/${eventId}/analyze`, { stock_ids: stockIds });
  }

  async getEventAnalysisStatus(eventId: string): Promise<any> {
    return apiService.get<any>(`/events/${eventId}/analysis/status`);
  }

  async getStockEventHistory(symbol: string): Promise<any> {
    return apiService.get<any>(`/events/stock/${symbol}`);
  }

  async analyzeStockEvents(symbol: string): Promise<any> {
    return apiService.post<any>(`/events/stock/${symbol}/analyze`);
  }

  async getEventCategories(): Promise<any> {
    return apiService.get<any>('/events/categories');
  }

  async prepopulateEvents(): Promise<any> {
    return apiService.post<any>('/events/prepopulate');
  }

  // ============================================================================
  // VALUE STOCK METHODS
  // ============================================================================

  async getValueStocks(options?: {
    min_score?: number;
    limit?: number;
    sector?: string;
    sort_by?: string;
    sort_order?: string;
  }): Promise<any> {
    const params = new URLSearchParams();
    if (options?.min_score) params.append('min_score', options.min_score.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.sector) params.append('sector', options.sector);
    if (options?.sort_by) params.append('sort_by', options.sort_by);
    if (options?.sort_order) params.append('sort_order', options.sort_order);
    
    const queryString = params.toString();
    return apiService.get<any>(`/strategies/value-stocks${queryString ? `?${queryString}` : ''}`);
  }

  async getStockValueScore(symbol: string): Promise<any> {
    return apiService.get<any>(`/strategies/value-stocks/${enc(symbol)}`);
  }

  // ============================================================================
  // SWING TRADE METHODS
  // ============================================================================

  async getSwingSignals(options?: {
    signal_type?: string;
    status?: string;
    min_score?: number;
    limit?: number;
    sort_by?: string;
  }): Promise<any> {
    const params = new URLSearchParams();
    if (options?.signal_type) params.append('signal_type', options.signal_type);
    if (options?.status) params.append('status', options.status);
    if (options?.min_score) params.append('min_score', options.min_score.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.sort_by) params.append('sort_by', options.sort_by);
    
    const queryString = params.toString();
    return apiService.get<any>(`/strategies/swing-signals${queryString ? `?${queryString}` : ''}`);
  }

  async getStockSwingSignals(symbol: string): Promise<any> {
    return apiService.get<any>(`/strategies/swing-signals/${enc(symbol)}`);
  }

  // ============================================================================
  // DATE RANGE COMPARISON METHODS
  // ============================================================================

  async getDateRangeComparison(options: {
    start_date: string;
    end_date: string;
    favorites_only?: boolean;
    min_change_pct?: number;
    sort_by?: string;
    sort_order?: string;
    limit?: number;
  }): Promise<any> {
    const params = new URLSearchParams();
    params.append('start_date', options.start_date);
    params.append('end_date', options.end_date);
    if (options.favorites_only) params.append('favorites_only', 'true');
    if (options.min_change_pct) params.append('min_change_pct', options.min_change_pct.toString());
    if (options.sort_by) params.append('sort_by', options.sort_by);
    if (options.sort_order) params.append('sort_order', options.sort_order);
    if (options.limit) params.append('limit', options.limit.toString());
    
    return apiService.get<any>(`/strategies/date-range-comparison?${params.toString()}`);
  }

  // ============================================================================
  // FAVORITES-FILTERED METHODS
  // ============================================================================

  async getValueStocksFavorites(options?: {
    min_score?: number;
    sort_by?: string;
    sort_order?: string;
  }): Promise<any> {
    const params = new URLSearchParams();
    if (options?.min_score) params.append('min_score', options.min_score.toString());
    if (options?.sort_by) params.append('sort_by', options.sort_by);
    if (options?.sort_order) params.append('sort_order', options.sort_order);
    
    const queryString = params.toString();
    return apiService.get<any>(`/strategies/value-stocks/favorites${queryString ? `?${queryString}` : ''}`);
  }

  async getSwingSignalsFavorites(options?: {
    signal_type?: string;
    status?: string;
    min_score?: number;
    sort_by?: string;
  }): Promise<any> {
    const params = new URLSearchParams();
    if (options?.signal_type) params.append('signal_type', options.signal_type);
    if (options?.status) params.append('status', options.status);
    if (options?.min_score) params.append('min_score', options.min_score.toString());
    if (options?.sort_by) params.append('sort_by', options.sort_by);
    
    const queryString = params.toString();
    return apiService.get<any>(`/strategies/swing-signals/favorites${queryString ? `?${queryString}` : ''}`);
  }

  async analyzeValueStocksFavorites(): Promise<any> {
    return apiService.post<any>('/strategies/value-stocks/analyze/favorites');
  }

  async analyzeSwingSignalsFavorites(): Promise<any> {
    return apiService.post<any>('/strategies/swing-signals/analyze/favorites');
  }

  async analyzeSwingSignals(): Promise<any> {
    return apiService.post<any>('/strategies/swing-signals/analyze');
  }

  async getSectors(): Promise<any> {
    return apiService.get<any>('/stocks/sectors');
  }

  async fetchSectors(favoritesOnly: boolean = true): Promise<any> {
    return apiService.post<any>('/stocks/fetch-sectors', { favorites_only: favoritesOnly });
  }

  async getStrategyAnalysisStatus(): Promise<any> {
    return apiService.get<any>('/strategies/analysis/status');
  }
}

export const stockService = new StockService();